import { filterKitchenOrderItems } from "./orderModel";
import {
  assertRuntimeCapability,
  RUNTIME_CAPABILITIES,
} from "./runtimeEnvironment";

const MOZZO_ANDROID_PRINT_RESULT_EVENT = "mozzo-android-print-result";
const MOZZO_ANDROID_MICROPHONE_PERMISSION_EVENT =
  "mozzo-android-microphone-permission-result";
const DEFAULT_ANDROID_PRINT_TIMEOUT_MS = 20000;
const DEFAULT_ANDROID_PERMISSION_TIMEOUT_MS = 15000;

const getMozzoAndroidBridge = () => {
  if (typeof window === "undefined" || !window.MozzoAndroid) {
    return null;
  }

  return window.MozzoAndroid;
};

const getMozzoAndroidBridgeMethod = (methodName) => {
  const bridge = getMozzoAndroidBridge();

  if (!bridge) {
    return null;
  }

  const candidate = bridge[methodName];

  if (candidate === undefined || candidate === null) {
    return null;
  }

  return (...args) => bridge[methodName](...args);
};

const normalizeAndroidEventDetail = (detail) => {
  if (!detail || typeof detail !== "object") {
    return {
      ok: false,
      message: "No recibimos una respuesta valida desde Mozzo Kiosk.",
      printerName: "",
    };
  }

  return {
    ok: detail.ok === true,
    message: String(detail.message || "").trim(),
    printerName: String(detail.printerName || "").trim(),
  };
};

const formatAndroidTicketDateParts = (value) => {
  const fallbackDate = new Date();
  const sourceDate = value ? new Date(value) : fallbackDate;
  const safeDate = Number.isNaN(sourceDate.getTime()) ? fallbackDate : sourceDate;

  return {
    date: safeDate.toLocaleDateString("es-AR"),
    time: safeDate.toLocaleTimeString("es-AR", {
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
};

const buildAndroidTicketItems = (order) => {
  const items = Array.isArray(order?.items) ? order.items : [];

  return items.map((item) => {
    const notes = [];

    if (Array.isArray(item?.selectionDetails)) {
      item.selectionDetails
        .map((detail) => String(detail || "").trim())
        .filter(Boolean)
        .forEach((detail) => notes.push(detail));
    }

    if (String(item?.notes || "").trim()) {
      notes.push(String(item.notes || "").trim());
    }

    return {
      quantity: Math.max(1, Number(item?.quantity || 1)),
      name:
        String(
          item?.productName ||
            item?.sourceProductName ||
            item?.name ||
            item?.nombre ||
            "Producto"
        ).trim() || "Producto",
      notes: notes.join(" | "),
    };
  });
};

const buildAndroidKitchenTicketItems = (order) =>
  buildAndroidTicketItems({
    ...order,
    items: filterKitchenOrderItems(order?.items),
  });

export const isMozzoAndroidBridgeAvailable = () =>
  Boolean(getMozzoAndroidBridgeMethod("printTicket"));

export const canRequestMozzoAndroidMicrophonePermission = () =>
  Boolean(getMozzoAndroidBridgeMethod("requestMicrophonePermission"));

export const getMozzoAndroidPrinterStatus = () => {
  const getPrinterStatus = getMozzoAndroidBridgeMethod("getPrinterStatus");

  if (!getPrinterStatus) {
    return null;
  }

  try {
    const rawStatus = getPrinterStatus();
    return rawStatus ? JSON.parse(rawStatus) : null;
  } catch (error) {
    return null;
  }
};

export const openMozzoAndroidPrinterSettings = () => {
  const openPrinterSettings = getMozzoAndroidBridgeMethod("openPrinterSettings");

  if (!openPrinterSettings) {
    return false;
  }

  try {
    openPrinterSettings();
    return true;
  } catch (error) {
    return false;
  }
};

export const requestMozzoAndroidMicrophonePermission = ({
  timeoutMs = DEFAULT_ANDROID_PERMISSION_TIMEOUT_MS,
} = {}) =>
  new Promise((resolve, reject) => {
    const requestMicrophonePermission = getMozzoAndroidBridgeMethod(
      "requestMicrophonePermission"
    );

    if (!requestMicrophonePermission) {
      resolve(null);
      return;
    }

    let settled = false;
    let timeoutId = 0;

    const cleanup = () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }

      window.removeEventListener(
        MOZZO_ANDROID_MICROPHONE_PERMISSION_EVENT,
        handlePermissionResult
      );
    };

    const settle = (callback) => {
      if (settled) {
        return;
      }

      settled = true;
      cleanup();
      callback();
    };

    const handlePermissionResult = (event) => {
      const detail = normalizeAndroidEventDetail(event?.detail);
      settle(() => resolve(detail.ok));
    };

    window.addEventListener(
      MOZZO_ANDROID_MICROPHONE_PERMISSION_EVENT,
      handlePermissionResult
    );

    timeoutId = window.setTimeout(() => {
      settle(() =>
        reject(
          new Error(
            "Mozzo Kiosk no respondio a tiempo al pedido de microfono."
          )
        )
      );
    }, Math.max(1000, Number(timeoutMs || DEFAULT_ANDROID_PERMISSION_TIMEOUT_MS)));

    try {
      requestMicrophonePermission();
    } catch (error) {
      settle(() =>
        reject(
          error instanceof Error
            ? error
            : new Error(
                "No pudimos pedir el permiso de microfono desde Mozzo Kiosk."
              )
        )
      );
    }
  });

export const buildMozzoAndroidCustomerTicketPayload = ({
  order,
  restaurantName,
  paymentMethodLabel = "",
  footer = "",
  paperWidthMm = 58,
}) => {
  const { date, time } = formatAndroidTicketDateParts(order?.orderedAt);
  const items = buildAndroidKitchenTicketItems(order);
  const serviceType = String(order?.serviceType || "").trim().toLowerCase();
  const serviceTypeLabel =
    serviceType === "takeaway"
      ? "Para llevar"
      : serviceType === "dine_in"
        ? "Comer aqui"
        : "";

  return {
    type: "customer_ticket",
    orderNumber: Number(order?.preparationNumber || 0) || String(order?.id || "").trim(),
    restaurantName: String(restaurantName || "MozzoApp").trim() || "MozzoApp",
    customerName: String(order?.customerName || "").trim(),
    serviceType: serviceTypeLabel,
    date,
    time,
    paymentMethod: String(paymentMethodLabel || "").trim(),
    total: Number(order?.pricing?.finalTotal ?? order?.totalAmount ?? 0) || 0,
    items,
    footer:
      String(footer || "").trim() ||
      "Retire cuando aparezca su numero en pantalla",
    paperWidthMm: paperWidthMm >= 80 ? 80 : 58,
  };
};

const resolveKitchenOrderNumber = (order) => {
  const mesa = String(order?.mesa || order?.tableLabel || order?.displayName || "").trim();
  if (mesa) {
    return mesa;
  }

  const preparationNumber = Number(order?.preparationNumber || 0);
  if (Number.isFinite(preparationNumber) && preparationNumber > 0) {
    return `Retiro ${String(Math.floor(preparationNumber)).padStart(3, "0")}`;
  }

  return String(order?.id || "").trim();
};

export const buildMozzoAndroidKitchenTicketPayload = ({
  order,
  restaurantName,
  paperWidthMm = 58,
}) => {
  const { date, time } = formatAndroidTicketDateParts(order?.orderedAt);
  const items = buildAndroidTicketItems(order);
  const channelLabel = String(
    order?.canal || order?.channelLabel || order?.sourceChannelLabel || ""
  ).trim();

  return {
    type: "kitchen_ticket",
    orderNumber: resolveKitchenOrderNumber(order),
    restaurantName: String(restaurantName || "MozzoApp").trim() || "MozzoApp",
    customerName: String(order?.customerName || order?.dinerName || "").trim(),
    serviceType: channelLabel || "Comanda cocina",
    date,
    time,
    paymentMethod: "",
    total: 0,
    items,
    footer: "Comanda generada desde MozzoApp para cocina",
    paperWidthMm: paperWidthMm >= 80 ? 80 : 58,
  };
};

export const printTicketWithMozzoAndroid = (
  payload,
  { timeoutMs = DEFAULT_ANDROID_PRINT_TIMEOUT_MS } = {}
) => {
  assertRuntimeCapability(RUNTIME_CAPABILITIES.LOCAL_PRINTING);

  return new Promise((resolve, reject) => {
    const printTicket = getMozzoAndroidBridgeMethod("printTicket");

    if (!printTicket) {
      reject(new Error("Mozzo Kiosk no detecto el puente Android de impresion."));
      return;
    }

    let settled = false;
    let timeoutId = 0;

    const cleanup = () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }

      window.removeEventListener(
        MOZZO_ANDROID_PRINT_RESULT_EVENT,
        handleAndroidPrintResult
      );
    };

    const settle = (callback) => {
      if (settled) {
        return;
      }

      settled = true;
      cleanup();
      callback();
    };

    const handleAndroidPrintResult = (event) => {
      const detail = normalizeAndroidEventDetail(event?.detail);

      if (detail.ok) {
        settle(() => resolve(detail));
        return;
      }

      settle(() =>
        reject(
          new Error(
            detail.message || "Mozzo Kiosk no pudo imprimir el ticket."
          )
        )
      );
    };

    window.addEventListener(
      MOZZO_ANDROID_PRINT_RESULT_EVENT,
      handleAndroidPrintResult
    );

    timeoutId = window.setTimeout(() => {
      settle(() =>
        reject(
          new Error(
            "La app Mozzo Kiosk no respondio a tiempo al pedido de impresion."
          )
        )
      );
    }, Math.max(1000, Number(timeoutMs || DEFAULT_ANDROID_PRINT_TIMEOUT_MS)));

    try {
      printTicket(JSON.stringify(payload));
    } catch (error) {
      settle(() =>
        reject(
          error instanceof Error
            ? error
            : new Error("No pudimos invocar la impresion nativa de Android.")
        )
      );
    }
  });
};
